import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useRef } from 'react';
import { delay } from '@olivierpascal/helpers';
import { Labeled } from '@sixui/ui';
import Konva from 'konva';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { ISize } from '~/helpers/types';
import type { IApiFileUploadProgressEvent } from '~/lib/api/modules/Files';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ITimeUnit } from '~/helpers/types';
import { useApi } from '~/lib/api';
import { IApiFileUploadStatus } from '~/lib/api/modules/Files';
import { getAppSettings } from '~/lib/appSettings';
import { ImageCreatorFormCanvas } from './ImageCreatorFormCanvas';
import { ImageCreatorFormMainSection } from './ImageCreatorFormMainSection';

export type IImageCreatorFormValues = {
  backgroundColor?: string;
  backgroundGradientColor?: string;
  foregroundColor?: string;
  icon?: string;
};

export type IImageCreatorFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IImageCreatorFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    defaultValues?: DefaultValues<IImageCreatorFormValues>;
    onSubmit?: SubmitHandler<IImageCreatorFormValues>;
    stageSize: ISize;
    appId: string;
    onProgressChange?: (event: IApiFileUploadProgressEvent) => void;
    onSuccess: (id: string, file: File) => void;
    abortController?: AbortController;
  };

export const ImageCreatorForm: React.FC<IImageCreatorFormProps> = (props) => {
  const {
    defaultValues,
    onSubmit,
    stageSize,
    appId,
    onSuccess,
    onProgressChange,
    abortController,
    ...other
  } = props;
  const { t } = useTranslation();
  const appSettings = getAppSettings();
  const defaultBackgroundColor =
    defaultValues?.backgroundColor ?? appSettings.theme.appDefaultPrimaryColor;
  const stageRef = useRef<Konva.Stage>(null);
  const api = useApi();

  const form = useForm<IImageCreatorFormValues>({
    defaultValues: {
      ...defaultValues,
      backgroundColor: defaultBackgroundColor,
    },
  });

  return (
    <ContentBlock.FormBody
      form={form}
      {...other}
      onSubmit={(values) => {
        onSubmit?.(values);

        const dataUri = stageRef.current?.toDataURL();
        if (!dataUri) {
          throw new Error('Unable to get construct image.');
        }

        return fetch(dataUri)
          .then((response) => response.blob())
          .then((blob) => {
            const name = `user-generated-${Date.now()}.png`;
            const type = blob.type;
            const size = blob.size;

            const uploadSlots = api.files.prepareForUpload(appId, {
              files: [{ name, type, size }],
              metadata: {
                opts: JSON.stringify({ keepRatio: true }),
              },
            });

            return Promise.all([blob, name, uploadSlots]);
          })
          .then(async ([blob, name, uploadSlots]) => {
            const uploadSlot = uploadSlots[0];
            if (!uploadSlot) {
              throw new Error('Unable to get upload slot.');
            }

            const file = new File([blob], name, { type: blob.type });

            const uploadResponse = api.files.upload(
              file,
              uploadSlot.url,
              onProgressChange,
              abortController,
            );

            return Promise.all([uploadSlot.id, file, uploadResponse]);
          })
          .then(async ([fileId, file]) => {
            // This is a ugly hack to wait for the file to be processed.
            // TODO: use a FileUploadManager once ready.

            let isReady = false;
            while (!isReady && !abortController?.signal.aborted) {
              const picture = await api.pictures.get(appId, fileId);
              if (picture.status === IApiFileUploadStatus.READY) {
                isReady = true;
              } else {
                await delay(1 * ITimeUnit.SECOND);
              }
            }

            if (isReady) {
              onSuccess(fileId, file);
            } else {
              throw new Error('File not ready.');
            }
          });
      }}
    >
      <ImageCreatorFormMainSection form={form} appId={appId} />

      <Labeled label={t('ImageCreatorForm.field.preview.label')}>
        <ImageCreatorFormCanvas
          stageRef={stageRef}
          form={form}
          stageSize={stageSize}
          defaultBackgroundColor={defaultBackgroundColor}
        />
      </Labeled>
    </ContentBlock.FormBody>
  );
};
